import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * The single loading state for Appointments (`/home/appointments`).
 *
 * Opening it used to walk through a bare route spinner (the `<Suspense>`
 * fallback), the `FeatureRoute` "Loading..." spinner, a blank `RoleRoute`
 * frame, a green ring while the meeting-platform status loaded and — while the
 * workspace was still unknown, which keeps the event-types query disabled — the
 * "Create an event" empty state. All of those now draw this: the calendar
 * sidebar, the toolbar and the week grid, so the page appears once and only
 * the meetings change when data lands.
 */

const DAY_COLUMNS = 7;
const HOUR_ROWS = 9;

/** Mirrors `AppointmentsCalendarSidebar` (272px, desktop only). */
const SidebarSkeleton: React.FC = () => (
  <aside className="flex h-full w-[272px] shrink-0 flex-col border-r border-[#E4E7EC] bg-white">
    <div className="px-4 pb-3 pt-6">
      <Skeleton className="h-9 w-full rounded-lg" />
    </div>
    <div className="px-3 pb-3">
      <div className="mb-1 flex h-7 items-center justify-between px-1">
        <Skeleton className="h-4 w-24 rounded" />
        <Skeleton className="h-4 w-12 rounded" />
      </div>
      <div className="mb-0.5 grid grid-cols-7">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="flex h-6 items-center justify-center">
            <Skeleton className="h-2.5 w-3 rounded" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {Array.from({ length: 35 }).map((_, i) => (
          <div key={i} className="flex h-8 items-center justify-center">
            <Skeleton className="h-3 w-4 rounded" />
          </div>
        ))}
      </div>
    </div>
    <div className="mx-3 h-px bg-[#EAECF0]" />
    <div className="min-h-0 flex-1 space-y-4 overflow-hidden px-3 py-3">
      {[0, 1].map((section) => (
        <div key={section} className="space-y-2">
          <Skeleton className="h-3 w-28 rounded" />
          {[0, 1, 2].map((row) => (
            <div key={row} className="flex items-center gap-2 px-1">
              <Skeleton className="h-4 w-4 shrink-0 rounded-[4px]" />
              <Skeleton className="h-3.5 flex-1 rounded" />
            </div>
          ))}
        </div>
      ))}
    </div>
  </aside>
);

/** Mirrors `AppointmentsCalendar`'s toolbar and week grid card. */
const CalendarSkeleton: React.FC = () => (
  <div className="space-y-3">
    <div className="flex min-w-0 flex-row flex-nowrap items-center gap-2 overflow-hidden">
      <Skeleton className="hidden h-9 w-9 shrink-0 rounded-lg lg:block" />
      <Skeleton className="h-9 min-w-[120px] shrink basis-[220px] rounded-lg" />
      <Skeleton className="hidden h-9 w-[92px] shrink-0 rounded-lg sm:block" />
      <Skeleton className="hidden h-9 w-[92px] shrink-0 rounded-lg sm:block" />
      <div className="ml-auto flex shrink-0 items-center gap-2">
        <Skeleton className="hidden h-9 w-[180px] rounded-lg md:block" />
        <Skeleton className="h-9 w-9 rounded-lg" />
        <Skeleton className="h-4 w-[148px] rounded" />
        <Skeleton className="h-9 w-9 rounded-lg" />
      </div>
    </div>
    <div className="overflow-hidden rounded-2xl border border-[#EAECF0] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
      <div className="overflow-hidden bg-[#F9FAFB] p-3">
        <div className="flex gap-0.5">
          <div className="flex w-12 shrink-0 flex-col gap-0.5 pt-9">
            {Array.from({ length: HOUR_ROWS }).map((_, i) => (
              <div key={i} className="flex h-14 justify-end pr-1 pt-1">
                <Skeleton className="h-2.5 w-8 rounded" />
              </div>
            ))}
          </div>
          {Array.from({ length: DAY_COLUMNS }).map((_, day) => (
            <div key={day} className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="flex h-9 items-center justify-center">
                <Skeleton className="h-4 w-12 rounded" />
              </div>
              {Array.from({ length: HOUR_ROWS }).map((_, i) => (
                <div key={i} className="h-14 rounded-md bg-white" />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const AppointmentsPageSkeleton: React.FC = () => (
  <div
    className="flex h-full min-h-0 w-full bg-white"
    aria-busy="true"
    aria-label="Loading appointments"
    data-testid="appointments-page-skeleton"
  >
    <div className="hidden h-full lg:block">
      <SidebarSkeleton />
    </div>
    <div className="min-w-0 flex-1 overflow-hidden">
      <div className="p-4 lg:p-6">
        <CalendarSkeleton />
      </div>
    </div>
  </div>
);

export default AppointmentsPageSkeleton;
